/**
 * El catálogo de textos que propone el agente (docs/activa/FASE-3-PLAN.md, «Mensajes»): plantillas escritas de antemano
 * por la temperatura del grupo y el tono de la marca (Branding), con dos versiones cada una («Otra versión» pasa a la
 * siguiente). Lógica pura. Nació «sin IA» (Alan, 14/10); desde el 01/11/2026 la IA escribe y el operador aprueba
 * («Escribir con IA», `components/EscribirConIa.tsx`, apagada sin la clave de Anthropic en el servidor), y este catálogo
 * sigue siendo el texto de arranque de cada grupo y la opción de quien no la use.
 *
 * El texto se arma en tres partes: la apertura (de la temperatura y el tono), la oferta (el cupón, el mínimo y la
 * vigencia; sin oferta, un cierre sin descuento) y, si hay cupón, los descuentos que la marca ya tiene, nombrados por
 * separado y nunca como un total. A quien gastaba mucho y está dormido, la apertura agradece: el texto no arranca por el
 * número.
 *
 * Las variables son las del backend: {nombre}, {sucursal}, {días sin comprar} y {cupón}. El código del cupón nace al
 * enviar (fase 4).
 */

import type { TonoMarca } from "../../branding/types";
import type { DescuentoVigente } from "../../descuentos/types";
import { pesos } from "../../segmentaciones/logic/segmentar";
import type { Temperatura } from "../types";

interface Plantilla {
  apertura: string;
  /** con oferta («{oferta}» es el %) y sin oferta */
  asunto: { con: string; sin: string };
  /** cómo cierra sin descuento */
  sinOferta: string;
}

const CATALOGO: Record<Temperatura, Record<TonoMarca, [Plantilla, Plantilla]>> = {
  compra_seguido: {
    cercano: [
      { apertura: "Hola {nombre}, gracias por elegirnos siempre.", asunto: { con: "{nombre}, esta semana es tuya", sin: "{nombre}, esta semana hay novedades" }, sinOferta: "Esta semana tenemos novedades. Te esperamos." },
      { apertura: "{nombre}, sos de los que más nos eligen y queremos agradecerte.", asunto: { con: "Un mimo para vos, {nombre}", sin: "Gracias por estar, {nombre}" }, sinOferta: "Te esperamos esta semana, como siempre." },
    ],
    divertido: [
      { apertura: "¡{nombre}! Ya sos de la casa.", asunto: { con: "{nombre}, esto es para los de la casa", sin: "¿Qué hay de nuevo, {nombre}?" }, sinOferta: "Tenemos novedades que te van a gustar. ¿Te venís esta semana?" },
      { apertura: "{nombre}, ya perdimos la cuenta de tus pedidos (y nos encanta).", asunto: { con: "Para los de siempre, {nombre}", sin: "Novedades para los de siempre" }, sinOferta: "Esta semana hay novedades. Avisamos por si te tentás." },
    ],
    elegante: [
      { apertura: "{nombre}, gracias por tu confianza de siempre.", asunto: { con: "Un reconocimiento para vos", sin: "Gracias por elegirnos" }, sinOferta: "Será un gusto recibirte otra vez esta semana." },
      { apertura: "{nombre}, es un gusto tenerte entre nuestros clientes de siempre.", asunto: { con: "Una atención para vos, {nombre}", sin: "Siempre es un gusto recibirte" }, sinOferta: "Te esperamos con las novedades de la semana." },
    ],
    directo: [
      { apertura: "{nombre}, gracias por volver siempre.", asunto: { con: "{oferta} para vos, {nombre}", sin: "Novedades de la semana" }, sinOferta: "Esta semana hay novedades. Te esperamos." },
      { apertura: "Hola {nombre}.", asunto: { con: "Tu beneficio de la semana", sin: "Esta semana, novedades" }, sinOferta: "Pasá cuando quieras: hay novedades." },
    ],
  },
  rompio_ritmo: {
    cercano: [
      { apertura: "Hola {nombre}, hace {días sin comprar} días que no pasás por {sucursal}.", asunto: { con: "Te guardamos un {oferta}", sin: "Hace rato que no pedís, {nombre}" }, sinOferta: "Lo de siempre te espera, con algunas novedades. ¿Te tentás esta semana?" },
      { apertura: "{nombre}, en {sucursal} te estamos esperando.", asunto: { con: "{nombre}, te guardamos un {oferta}", sin: "{nombre}, te estamos esperando" }, sinOferta: "Te esperamos esta semana." },
    ],
    divertido: [
      { apertura: "{nombre}, ¿todo bien? Hace {días sin comprar} días que no sabemos nada de vos.", asunto: { con: "{nombre}, ¿nos olvidaste? Va un {oferta}", sin: "{nombre}, ¿nos olvidaste?" }, sinOferta: "Volvé y contanos qué te tienta." },
      { apertura: "{nombre}, en {sucursal} preguntan por vos.", asunto: { con: "Te extrañamos (y te dejamos un {oferta})", sin: "En {sucursal} preguntan por vos" }, sinOferta: "¿Arrancamos de nuevo esta semana?" },
    ],
    elegante: [
      { apertura: "{nombre}, hace {días sin comprar} días que no te vemos y queríamos saludarte.", asunto: { con: "Te reservamos un {oferta}", sin: "Nos gustaría volver a verte" }, sinOferta: "Será un gusto recibirte otra vez." },
      { apertura: "{nombre}, en {sucursal} te extrañamos.", asunto: { con: "Un {oferta} reservado para vos", sin: "Te extrañamos en {sucursal}" }, sinOferta: "Te esperamos cuando quieras." },
    ],
    directo: [
      { apertura: "{nombre}, hace {días sin comprar} días de tu último pedido.", asunto: { con: "{oferta} para tu próximo pedido", sin: "Hace {días sin comprar} días de tu último pedido" }, sinOferta: "Te esperamos." },
      { apertura: "Hola {nombre}.", asunto: { con: "Volvé con {oferta}", sin: "Volvé cuando quieras" }, sinOferta: "Volvé cuando quieras." },
    ],
  },
  probo_una_vez: {
    cercano: [
      { apertura: "Hola {nombre}, nos encantó tenerte la primera vez.", asunto: { con: "Tu segundo pedido viene con {oferta}", sin: "¿Volvés por el segundo, {nombre}?" }, sinOferta: "Te esperamos para tu segundo pedido." },
      { apertura: "{nombre}, ¿qué tal si repetimos?", asunto: { con: "{nombre}, repetimos con {oferta}", sin: "{nombre}, ¿repetimos?" }, sinOferta: "Te esperamos para la segunda." },
    ],
    divertido: [
      { apertura: "{nombre}, el primer pedido fue el ensayo.", asunto: { con: "¿Repetimos, {nombre}? Va con {oferta}", sin: "¿Repetimos, {nombre}?" }, sinOferta: "El segundo es el que cuenta. Te esperamos." },
      { apertura: "{nombre}, una vez es casualidad; dos, costumbre.", asunto: { con: "La segunda vez viene con {oferta}", sin: "¿Hacemos costumbre, {nombre}?" }, sinOferta: "¿Hacemos costumbre esta semana?" },
    ],
    elegante: [
      { apertura: "{nombre}, será un gusto recibirte otra vez.", asunto: { con: "Una invitación para volver", sin: "Una invitación para volver" }, sinOferta: "Te esperamos para tu próximo pedido." },
      { apertura: "{nombre}, gracias por elegirnos la primera vez.", asunto: { con: "Para tu próxima visita, {oferta}", sin: "Gracias por elegirnos" }, sinOferta: "Será un gusto atenderte otra vez." },
    ],
    directo: [
      { apertura: "Hola {nombre}.", asunto: { con: "{oferta} en tu segundo pedido", sin: "Tu segundo pedido" }, sinOferta: "Te esperamos para el segundo." },
      { apertura: "{nombre}, ya probaste: ahora repetí.", asunto: { con: "Repetí con {oferta}", sin: "¿Repetís, {nombre}?" }, sinOferta: "Te esperamos." },
    ],
  },
  hace_mucho: {
    cercano: [
      { apertura: "Hola {nombre}, pasó un tiempo y te extrañamos.", asunto: { con: "Te extrañamos: {oferta} para volver", sin: "Te extrañamos, {nombre}" }, sinOferta: "Volvé cuando quieras: te esperamos." },
      { apertura: "{nombre}, hace mucho que no nos vemos.", asunto: { con: "{nombre}, volvé con {oferta}", sin: "{nombre}, hace mucho que no nos vemos" }, sinOferta: "Te esperamos cuando quieras." },
    ],
    divertido: [
      { apertura: "{nombre}, ¡cuánto tiempo! Pensamos que te habías mudado.", asunto: { con: "{nombre}, ¡cuánto tiempo! Va un {oferta}", sin: "{nombre}, ¡cuánto tiempo!" }, sinOferta: "¿Nos damos otra oportunidad?" },
      { apertura: "{nombre}, te buscamos por todos lados.", asunto: { con: "Te encontramos: {oferta} para volver", sin: "Te buscamos por todos lados, {nombre}" }, sinOferta: "Volvé, que te esperamos." },
    ],
    elegante: [
      { apertura: "{nombre}, hace un tiempo que no te vemos y nos gustaría recibirte otra vez.", asunto: { con: "Un {oferta} para volver a vernos", sin: "Nos gustaría volver a verte" }, sinOferta: "Te esperamos cuando quieras." },
      { apertura: "{nombre}, te extrañamos.", asunto: { con: "Una invitación con {oferta}", sin: "Una invitación para volver" }, sinOferta: "Será un gusto recibirte." },
    ],
    directo: [
      { apertura: "{nombre}, hace mucho que no pedís.", asunto: { con: "{oferta} para volver", sin: "Hace mucho que no pedís" }, sinOferta: "Te esperamos." },
      { apertura: "Hola {nombre}.", asunto: { con: "Volvé con {oferta}", sin: "Volvé cuando quieras" }, sinOferta: "Volvé cuando quieras." },
    ],
  },
  // gastaban mucho: la apertura y el asunto agradecen; el número va después
  alto_valor_dormido: {
    cercano: [
      { apertura: "Hola {nombre}, gracias por todas las veces que nos elegiste.", asunto: { con: "Gracias, {nombre}: te guardamos algo", sin: "Gracias por todas las veces, {nombre}" }, sinOferta: "Te extrañamos y queremos volver a verte." },
      { apertura: "{nombre}, fuiste de nuestros clientes más fieles y te extrañamos.", asunto: { con: "{nombre}, te extrañamos", sin: "{nombre}, te extrañamos" }, sinOferta: "Volvé cuando quieras: te esperamos." },
    ],
    divertido: [
      { apertura: "{nombre}, fuiste de los mejores clientes que tuvimos. Y lo sabés.", asunto: { con: "{nombre}, esto es para vos", sin: "{nombre}, ¿te acordás de nosotros?" }, sinOferta: "Te esperamos con todo." },
      { apertura: "{nombre}, desde que no venís, no es lo mismo.", asunto: { con: "No es lo mismo sin vos, {nombre}", sin: "No es lo mismo sin vos" }, sinOferta: "¿Volvés?" },
    ],
    elegante: [
      { apertura: "{nombre}, queremos agradecerte por todas las veces que nos elegiste.", asunto: { con: "Con agradecimiento, {nombre}", sin: "Con agradecimiento" }, sinOferta: "Será un gusto recibirte otra vez." },
      { apertura: "{nombre}, tu confianza fue muy valiosa para nosotros.", asunto: { con: "Una atención especial para vos", sin: "Gracias por tu confianza" }, sinOferta: "Te esperamos cuando quieras." },
    ],
    directo: [
      { apertura: "{nombre}, gracias por tantos pedidos.", asunto: { con: "Gracias, {nombre}", sin: "Gracias, {nombre}" }, sinOferta: "Te esperamos." },
      { apertura: "Hola {nombre}, te extrañamos.", asunto: { con: "Te extrañamos, {nombre}", sin: "Te extrañamos, {nombre}" }, sinOferta: "Volvé cuando quieras." },
    ],
  },
};

/** Cuántas versiones hay de cada texto («Otra versión» da la vuelta). */
export const VERSIONES = 2;

const plantillaDe = (t: Temperatura, tono: TonoMarca, variante: number): Plantilla => {
  const lista = (CATALOGO[t] ?? CATALOGO.hace_mucho)[tono] ?? CATALOGO[t].cercano;
  const i = ((Math.trunc(variante) % lista.length) + lista.length) % lista.length;
  return lista[i];
};

/** «, sin mínimo,» o « desde $35.000»: va pegado a «en tu pedido». */
const conMinimo = (minimo: number | null) => (minimo === null ? ", sin mínimo," : ` desde ${pesos(minimo)}`);

/** La oferta en el tono de la marca. A quien rompió su ritmo, con la urgencia del vencimiento. */
export function fraseDeLaOferta(tono: TonoMarca, oferta: number, minimo: number | null, dias: number, urgente: boolean): string {
  const m = conMinimo(minimo);
  const vence = `Vale ${dias} ${dias === 1 ? "día" : "días"}`;
  switch (tono) {
    case "divertido":
      return `Te dejamos ${oferta}% de regalo en tu pedido${m} con el código {cupón}. ${vence}${urgente ? ": ¡que no se te pase!" : "."}`;
    case "elegante":
      return `Te reservamos un ${oferta}% en tu próximo pedido${m.startsWith(",") ? m : `${m},`} con el código {cupón}. ${vence}${urgente ? ", así que te esperamos pronto." : "."}`;
    case "directo":
      return `${oferta}% en tu pedido${m} con el código {cupón}. ${vence}${urgente ? ": después vence." : "."}`;
    default:
      return `Esta semana tenés ${oferta}% en tu pedido${m} con el código {cupón}. ${vence}${urgente ? ": no lo dejes pasar." : "."}`;
  }
}

/** «Condición» en el medio de una frase: con minúscula. */
const condicionEnFrase = (c: string) => {
  const t = c.trim().replace(/[.\s]+$/, "");
  return t ? t.charAt(0).toLocaleLowerCase("es-AR") + t.slice(1) : "";
};

const diaMes = (dia: string) => `${dia.slice(8, 10)}/${dia.slice(5, 7)}`;

/**
 * Los descuentos que la marca ya tiene, por separado y nunca como un total. «Aparte, sigue el 20% OFF TAKE AWAY de
 * siempre, retirando en el local.»; con fecha, «Aparte, hasta el 31/10 sigue el …».
 */
export function lineaDeVigentes(vigentes: DescuentoVigente[]): string {
  const uno = (d: DescuentoVigente) => {
    const c = condicionEnFrase(d.condicion);
    return { nombre: d.nombre.trim(), c, hasta: d.hasta };
  };
  const lista = vigentes.map(uno).filter((d) => d.nombre);
  if (!lista.length) return "";
  if (lista.length === 1) {
    const d = lista[0];
    const cola = d.c ? `, ${d.c}.` : ".";
    return d.hasta ? `Aparte, hasta el ${diaMes(d.hasta)} sigue el ${d.nombre}${cola}` : `Aparte, sigue el ${d.nombre} de siempre${cola}`;
  }
  const items = lista.map((d) => `${d.nombre}${d.c ? ` (${d.c})` : ""}${d.hasta ? ` hasta el ${diaMes(d.hasta)}` : ""}`);
  return `Aparte, siguen los de siempre: ${items.slice(0, -1).join(", ")} y ${items[items.length - 1]}.`;
}

export interface Composicion {
  temperatura: Temperatura;
  tono: TonoMarca;
  variante: number;
  oferta: number;
  minimo: number | null;
  /** los que siguen vigentes hoy */
  vigentes: DescuentoVigente[];
  vigenciaDias: number;
}

/** El asunto y el texto que propone el agente para un grupo. */
export function componerMensaje(c: Composicion): { asunto: string; texto: string } {
  const p = plantillaDe(c.temperatura, c.tono, c.variante);
  const conOferta = c.oferta > 0;
  const asunto = (conOferta ? p.asunto.con : p.asunto.sin).replace(/\{oferta\}/g, `${c.oferta}%`);
  const lineas = [p.apertura];
  if (conOferta) {
    lineas.push(fraseDeLaOferta(c.tono, c.oferta, c.minimo, c.vigenciaDias, c.temperatura === "rompio_ritmo"));
    const v = lineaDeVigentes(c.vigentes);
    if (v) lineas.push(v);
  } else {
    lineas.push(p.sinOferta);
  }
  return { asunto, texto: lineas.join("\n") };
}

/** Todas las plantillas, para los tests (que ninguna diga lo que no se dice). */
export const _todasLasPlantillas = (): Plantilla[] => Object.values(CATALOGO).flatMap((porTono) => Object.values(porTono).flat());
